import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Plus } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';

/**
 * `/leave/grants` (v0.4 休暇管理 TD-26、§6-1 L-2 のうち「付与にまつわる操作」)。
 *
 * `/leave/records` は残数・年5日進捗・有休管理簿を**閲覧専用**で持ち `useMutation` を1つも持たない。
 * その結果「休暇を付与する」手段がプロダクト上どこにも無かった。この画面がその欠落を埋める
 * (閲覧系は `/leave/records` に残し重複させない)。
 *
 * ⚠️ §3-4-0 H-4 / TD-22: 付与・調整・取消はすべて `leave_grant:manage`。バックエンドは
 *    `requireTenantWideGrantManage` (routes/leave-grants.ts) で全店舗スコープを要求するため、
 *    UI 側も `hasTenantWidePermission` で同じ条件を再現する (`LeaveRecordsPage.tsx` の
 *    canManageGrants と同じ判断)。押しても必ず 403 になるボタンは出さない。
 *    ⚠️ 現在 `permissions` には `leave_grant:manage` の `all_stores` 行しか無い (migration 0076) が、
 *    それに依存せず scope を見ること — 将来 own_store 行が追加された瞬間に判定が狂うため。
 * ⚠️ §6-3③ (D-284/D-93): 付与日数は必ず根拠を併記する。試算結果・付与一覧のどちらでも
 *    `basis` を人が読める形で出し、法定値テーブルを使った付与では `GET /legal-constants` から
 *    出典・最終確認日を引いて併記する。数値だけを出さない。
 * ⚠️ §6-3⑤ (D-279): 法定値が未確認の状態を隠さない。試算の `blockers` は必ず全件表示し、
 *    `unconfirmed_legal_value` が1件でもある間は本実行ボタンを押せなくする
 *    (誤った日数での付与は労基法39条の付与不足に直結するため fail-closed に倒す)。
 * ⚠️ H-4: 付与の取消は年5日取得義務の未達を隠蔽できる操作。確認ダイアログで理由入力を求め、
 *    その旨を文言で明示する。
 *
 * 設計: docs/design/v04-leave-management-detail.md §5-3 / §6-1 L-2 / §6-3
 */

// -----------------------------------------------------------------------------
// 型 (apps/api/src/routes/leave-grants.ts / lib/leave/accrual.ts の実装に対応)
// -----------------------------------------------------------------------------

/** `POST /leave/grants/run` が返す `results[].grants[]` は `lib/leave/accrual.ts` の
 *  `PlannedGrant` をそのまま返す (= camelCase)。`GET /leave/grants` の serializeGrant
 *  (snake_case) とは別の形であることに注意。 */
type PlannedGrant = {
  grantDate: string;
  grantedDays: number | null;
  grantedMinutes: number | null;
  expiresOn: string | null;
  capPeriodStart: string | null;
  capPeriodEnd: string | null;
  basis: Record<string, unknown>;
};

type AccrualBlocker = { kind: string; message: string; constantKey?: string; grantDate?: string };

type RunResponse = {
  dry_run: boolean;
  as_of: string;
  total_created: number;
  results: { employee_id: string; grants: PlannedGrant[]; blockers: AccrualBlocker[] }[];
};

type GrantRow = {
  grant_id: string; employee_id: string; leave_type_id: string;
  grant_date: string; granted_days: number | null; granted_minutes: number | null;
  expires_on: string | null; family_member_id: string | null; event_ref: string | null; event_code: string | null;
  cap_period_start: string | null; cap_period_end: string | null;
  is_five_day_obligation_target: boolean;
  obligation_period_start: string | null; obligation_period_end: string | null;
  basis: Record<string, unknown> | null; source: string;
  note: string | null; revoked_at: string | null; revoked_reason: string | null;
};

/** `GET /leave/consumptions` (§4-7 / D-312)。
 *  ⚠️ `leave_type_id` が null なのは「データが無い」のではなく **leave_visibility ラダーの段階が
 *  'type' 未満で伏せられた** 結果 (生理休暇・介護休暇のような機微な種別が日付付きで漏れるのを
 *  防ぐ境界)。UI で「不明」「-」のような欠損として出してはならない。 */
type ConsumptionRow = {
  consumption_id: string; employee_id: string; grant_id: string | null;
  consumed_on: string; unit: string;
  consumed_days: number | null; consumed_minutes: number | null;
  start_time: string | null; end_time: string | null;
  counts_toward_five_day_obligation: boolean;
  reversed_at: string | null; reversal_reason: string | null;
  leave_type_id: string | null;
};

type LeaveTypeOption = { leave_type_id: string; label: string; balance_model: string; statutory_kind: string | null };

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };

type LegalConstant = { constant_key: string; source_citation: string; last_verified_on: string };

const GRANT_SOURCE_LABELS: Record<string, string> = {
  statutory_auto: '自動付与', manual: '手動付与', migrated: '初期残高の移行',
  adjustment: '調整', carryover: '繰越',
};

/** 手動登録の画面から選べる由来。`statutory_auto` は付与エンジンだけが書く値、
 *  `adjustment`/`carryover` はそれぞれ調整・繰越の処理が書く値のため出さない。 */
const MANUAL_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'manual', label: '手動付与' },
  { value: 'migrated', label: '初期残高の移行' },
];

const CONSUMPTION_UNIT_LABELS: Record<string, string> = { day: '1日', half_day: '半日', hour: '時間単位' };

const BLOCKER_KIND_LABELS: Record<string, string> = {
  unconfirmed_legal_value: '法定値が未確認',
  missing_contracted_work_days: '週所定労働日数が未設定',
  missing_attendance_data: '出勤率を算出できない',
  attendance_below_threshold: '出勤率が基準未満',
  missing_family_targets: '対象家族が未登録',
  unified_date_not_supported: '斉一的取扱いは自動付与に未対応',
  unsupported_batch_model: '定期バッチでは自動付与しない種別',
  invalid_leave_type_config: '休暇種別の設定が不正',
};

/** §6-3③ の「根拠を必ず併記する」を満たすため、`basis` (制度ごとに形が異なる JSON) を
 *  raw dump ではなく日本語の項目名に変換する (LeaveAbsencesPage.tsx の BasisSummary と同型)。
 *  ラベル表に無いキーはキー名のまま出すフォールバックを持ち、将来 accrual.ts が項目を
 *  追加しても表示が落ちないようにする。 */
const BASIS_FIELD_LABELS: Record<string, string> = {
  tenure_months: '勤続月数 (基準日)',
  weekly_contracted_work_days: '週所定労働日数',
  weekly_contracted_minutes: '週所定労働時間 (分)',
  attendance_rate: '出勤率',
  table_used: '使用した法定値テーブル',
  engine_version: '付与エンジンの版',
  family_target_count: '対象家族の人数',
  cap_period_basis: '年度の起算方法',
};

const LEGAL_VALUE_LABELS: Record<string, string> = {
  annual_leave_grant_table_full_time: '年次有給休暇 付与日数表 (通常付与)',
  annual_leave_grant_table_proportional: '年次有給休暇 付与日数表 (比例付与)',
  annual_leave_proportional_threshold: '比例付与の判定基準',
  annual_leave_attendance_rate_threshold: '出勤率の基準',
  annual_leave_expiry_years: '年次有給休暇の時効年数',
  annual_leave_five_day_obligation: '年5日取得義務の基準',
};

const formatBasisValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'はい' : 'いいえ';
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatDays = (days: number | null, minutes: number | null): string => {
  const parts: string[] = [];
  if (days !== null) parts.push(`${days} 日`);
  if (minutes !== null && minutes > 0) parts.push(`${Math.floor(minutes / 60)} 時間${minutes % 60 > 0 ? `${minutes % 60} 分` : ''}`);
  return parts.length > 0 ? parts.join(' + ') : '—';
};

const formatConsumed = (r: ConsumptionRow): string => {
  const parts: string[] = [];
  if (r.consumed_days !== null && r.consumed_days > 0) parts.push(`${r.consumed_days} 日`);
  if (r.consumed_minutes !== null && r.consumed_minutes > 0) {
    parts.push(`${Math.floor(r.consumed_minutes / 60)} 時間${r.consumed_minutes % 60 > 0 ? `${r.consumed_minutes % 60} 分` : ''}`);
  }
  return parts.length > 0 ? parts.join(' + ') : '—';
};

const employeeLabel = (e: Employee) => `${e.family_name} ${e.given_name} (${e.employee_number})`;

const todayYmd = () => new Date().toISOString().slice(0, 10);

const dateInputCls = 'min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white';
const textInputCls = dateInputCls;
// ⚠️ type="number" のネイティブスピナーは抑制する (ui-guidelines §7.2/D3、
// AttendanceSettingsPage / LeaveTypesSettingsPage で確立済みのパターン)。
const numberInputCls = `${dateInputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

// -----------------------------------------------------------------------------
// 共有フック
// -----------------------------------------------------------------------------

/** `GET /employees` は company_admin のみ。`leave_grant:manage` は持つが company_admin ではない
 *  ユーザーもいるため、失敗を致命扱いにせず「氏名が引けない」だけに留める (retry しない)。 */
const useEmployees = () => useQuery<Employee[]>({
  queryKey: ['employees'],
  queryFn: () => apiFetch<Employee[]>('/employees'),
  retry: false,
});

/** `GET /leave-types` は `leave_type:view` を要求する。付与の実行者が必ず持つとは限らないため
 *  こちらも retry せず、取得できないときは操作を止めずに理由を表示する。 */
const useLeaveTypes = () => useQuery<LeaveTypeOption[]>({
  queryKey: ['leave-types-for-grants'],
  queryFn: async () => {
    const rows = await apiFetch<Array<Record<string, unknown>>>('/leave-types');
    return rows.map((r) => ({
      leave_type_id: r.leave_type_id as string,
      label: r.label as string,
      balance_model: (r.balance_model as string) ?? '',
      statutory_kind: (r.statutory_kind as string | null) ?? null,
    }));
  },
  retry: false,
});

/** 氏名・種別名の解決。取得できなければ ID にフォールバックする (権限差で一覧が引けないことがある)。 */
const useEmployeeNameResolver = () => {
  const { data: employees = [] } = useEmployees();
  return useMemo(() => {
    const map = new Map(employees.map((e) => [e.employee_id, employeeLabel(e)]));
    return (id: string) => map.get(id) ?? id;
  }, [employees]);
};

const useLeaveTypeNameResolver = () => {
  const { data: leaveTypes = [] } = useLeaveTypes();
  return useMemo(() => {
    const map = new Map(leaveTypes.map((lt) => [lt.leave_type_id, lt.label]));
    return (id: string) => map.get(id) ?? id;
  }, [leaveTypes]);
};

/** §6-3③ の「出典 / 最終確認日」。`GET /legal-constants` は認証のみで読める全テナント共通の
 *  参照カタログ (D-93: 各値に最終確認日を必ず表示する)。 */
const useLegalConstantMeta = () => {
  const { data } = useQuery<LegalConstant[]>({
    queryKey: ['legal-constants'],
    queryFn: () => apiFetch<LegalConstant[]>('/legal-constants'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return useMemo(() => new Map((data ?? []).map((r) => [r.constant_key, r])), [data]);
};

// -----------------------------------------------------------------------------
// 表示部品
// -----------------------------------------------------------------------------

/** ⚠️ §6-3③ / D-284 / D-93 の中核: 付与日数の**併記**は常時可視でなければならない。
 *  `<details>` に畳んだ根拠は「1クリック隠れている」状態であり、画面上は日数だけが見える。
 *  そこで「勤続◯ヶ月・週◯日・出勤率◯% / 出典 (最終確認 ◯)」の1行を常に出し、
 *  `<details>` 側は全項目を確認するための展開に役割を変える。 */
function BasisInlineSummary({ basis, legalMeta }: { basis: Record<string, unknown> | null; legalMeta: Map<string, LegalConstant> }) {
  if (!basis) {
    return <p className="text-xs text-text-sub">根拠情報の記録なし (手動付与・初期残高の移行等)</p>;
  }
  const parts: string[] = [];
  if (typeof basis.tenure_months === 'number') parts.push(`勤続${basis.tenure_months}ヶ月`);
  if (typeof basis.weekly_contracted_work_days === 'number') parts.push(`週所定${basis.weekly_contracted_work_days}日`);
  if (typeof basis.attendance_rate === 'number') parts.push(`出勤率${Math.round(basis.attendance_rate * 1000) / 10}%`);
  if (typeof basis.family_target_count === 'number') parts.push(`対象家族${basis.family_target_count}人`);
  const tableUsed = typeof basis.table_used === 'string' ? basis.table_used : null;
  const meta = tableUsed ? legalMeta.get(tableUsed) : undefined;
  if (parts.length === 0 && !tableUsed) {
    // ⚠️ ローカル実機検証で発見: 手動付与・初期残高の移行は basis に法定値系のキーを持たず
    // `source` / `note` / `manually_entered_by` だけを持つ。以前はここで「根拠情報の記録なし」と
    // 出していたが、**根拠は記録されている** (付与の由来が人の判断であること) ため誤り。
    // D-284 が求めるのは「根拠を凍結して見せる」ことであり、由来もその一部。
    const source = typeof basis.source === 'string' ? basis.source : null;
    if (source) {
      return (
        <p className="text-xs text-text-sub break-words">
          根拠: {GRANT_SOURCE_LABELS[source] ?? source}による登録
          {typeof basis.manually_entered_by === 'string' && <> (登録者: {basis.manually_entered_by})</>}
        </p>
      );
    }
    return <p className="text-xs text-text-sub">根拠情報の記録なし</p>;
  }
  return (
    <p className="text-xs text-text-sub break-words">
      根拠: {parts.join(' / ') || '—'}
      {tableUsed && <> ・ 出典: {meta ? `${meta.source_citation} (最終確認 ${meta.last_verified_on})` : (LEGAL_VALUE_LABELS[tableUsed] ?? tableUsed)}</>}
    </p>
  );
}

function BasisSummary({ basis, legalMeta }: { basis: Record<string, unknown>; legalMeta: Map<string, LegalConstant> }) {
  const entries = Object.entries(basis);
  const tableUsed = typeof basis.table_used === 'string' ? basis.table_used : null;
  const meta = tableUsed ? legalMeta.get(tableUsed) : undefined;

  if (entries.length === 0) {
    return <p className="text-sm text-text-sub">この付与には根拠情報が記録されていません。</p>;
  }
  return (
    <div className="space-y-2">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-text-sub">{BASIS_FIELD_LABELS[key] ?? key}</dt>
            <dd className="text-text-body break-words">
              {key === 'table_used' ? (LEGAL_VALUE_LABELS[String(value)] ?? formatBasisValue(value)) : formatBasisValue(value)}
            </dd>
          </div>
        ))}
      </dl>
      {/* ⚠️ §6-3③ / D-93: 法定値を使った付与では出典と最終確認日を必ず併記する。 */}
      {meta && (
        <p className="text-xs text-text-sub">
          出典: {meta.source_citation} / 最終確認日: {meta.last_verified_on}
        </p>
      )}
      {tableUsed && !meta && (
        <p className="text-xs text-text-sub">出典・最終確認日を取得できていません (法定値マスタの読み込みに失敗した可能性があります)。</p>
      )}
    </div>
  );
}

function BlockerList({ blockers }: { blockers: (AccrualBlocker & { employee_label?: string })[] }) {
  if (blockers.length === 0) return null;
  return (
    <ul className="space-y-2">
      {blockers.map((b, i) => (
        <li key={`${b.kind}-${b.constantKey ?? ''}-${b.grantDate ?? ''}-${i}`}
          className="flex items-start gap-2 border border-alert-border bg-alert-bg rounded-lg p-3">
          <AlertTriangle size={16} className="text-alert-text mt-0.5 shrink-0" aria-hidden />
          <div className="text-sm min-w-0">
            <p className="font-medium text-text-body">
              {b.employee_label ? `${b.employee_label} — ` : ''}{BLOCKER_KIND_LABELS[b.kind] ?? b.kind}
            </p>
            <p className="text-text-sub break-words">{b.message}</p>
            {b.constantKey && (
              <p className="text-xs text-text-sub mt-0.5">
                対象の法定値: {LEGAL_VALUE_LABELS[b.constantKey] ?? b.constantKey}
              </p>
            )}
            {b.grantDate && <p className="text-xs text-text-sub mt-0.5">対象の基準日: {b.grantDate}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** 社員を氏名・社員番号で選ぶセレクタ (LeaveAbsencesPage.tsx / VisibilityRulesSettingsPage.tsx と同型)。
 *  生の employee_id テキスト入力は入力ミスの温床になるため使わない。 */
function EmployeeSelector({
  value, onChange, placeholder = '社員を選ぶ', allowEmpty = false, emptyLabel = '(すべての社員)',
}: {
  value: string; onChange: (id: string) => void; placeholder?: string; allowEmpty?: boolean; emptyLabel?: string;
}) {
  const { data: employees = [], isError } = useEmployees();
  const EMPTY = '__empty__';
  if (isError) {
    return (
      <p className="text-sm text-text-sub">
        社員の一覧を取得できませんでした (社員情報の閲覧権限が必要です)。社員を指定した操作は行えません。
      </p>
    );
  }
  return (
    <Select value={value || EMPTY} onValueChange={(v) => onChange(v === EMPTY ? '' : v)}>
      <SelectTrigger className="w-full max-w-md min-h-[44px]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {allowEmpty && <SelectItem value={EMPTY}>{emptyLabel}</SelectItem>}
        {employees.map((e) => (
          <SelectItem key={e.employee_id} value={e.employee_id}>{employeeLabel(e)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function LeaveTypeSelector({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const t = useLabel();
  const { data: leaveTypes = [], isError } = useLeaveTypes();
  if (isError) {
    return (
      <p className="text-sm text-text-sub">
        {t('entity.leave_types', '休暇種別')}の一覧を取得できませんでした (休暇種別の閲覧権限が必要です)。
      </p>
    );
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full max-w-md min-h-[44px]">
        <SelectValue placeholder={`${t('entity.leave_types', '休暇種別')}を選ぶ`} />
      </SelectTrigger>
      <SelectContent>
        {leaveTypes.map((lt) => (
          <SelectItem key={lt.leave_type_id} value={lt.leave_type_id}>{lt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// -----------------------------------------------------------------------------
// 一括付与 (付与エンジンの実行)
// -----------------------------------------------------------------------------

function AccrualRunSection({ legalMeta }: { legalMeta: Map<string, LegalConstant> }) {
  const t = useLabel();
  const queryClient = useQueryClient();
  const { data: leaveTypes = [] } = useLeaveTypes();
  const employeeName = useEmployeeNameResolver();

  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [asOf, setAsOf] = useState(todayYmd());
  const [targetMode, setTargetMode] = useState<'all' | 'one'>('all');
  const [targetEmployeeId, setTargetEmployeeId] = useState('');
  const [preview, setPreview] = useState<RunResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const leaveTypeLabel = leaveTypes.find((lt) => lt.leave_type_id === leaveTypeId)?.label ?? '';

  /** 入力が変わったら試算結果は無効。古い試算のまま本実行させないため必ず捨てる。 */
  const resetPreview = () => setPreview(null);

  const buildBody = (dryRun: boolean) => ({
    leave_type_id: leaveTypeId,
    as_of: asOf,
    dry_run: dryRun,
    ...(targetMode === 'one' && targetEmployeeId ? { employee_ids: [targetEmployeeId] } : {}),
  });

  const previewMut = useMutation({
    mutationFn: () => apiFetch<RunResponse>('/leave/grants/run', { method: 'POST', body: JSON.stringify(buildBody(true)) }),
    onSuccess: (data) => setPreview(data),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '試算に失敗しました'),
  });

  const executeMut = useMutation({
    mutationFn: () => apiFetch<RunResponse>('/leave/grants/run', { method: 'POST', body: JSON.stringify(buildBody(false)) }),
    onSuccess: (data) => {
      setConfirmOpen(false);
      setPreview(null);
      toast.success(`${data.total_created} 件の付与を登録しました`);
      queryClient.invalidateQueries({ queryKey: ['leave-grants'] });
      queryClient.invalidateQueries({ queryKey: ['leave-me-balance'] });
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
      queryClient.invalidateQueries({ queryKey: ['leave-five-day-obligation'] });
    },
    onError: (e) => {
      setConfirmOpen(false);
      toast.error(e instanceof ApiClientError ? e.message : '付与の実行に失敗しました');
    },
  });

  const canPreview = leaveTypeId !== '' && asOf !== '' && (targetMode === 'all' || targetEmployeeId !== '');

  const allBlockers = useMemo(
    () => (preview?.results ?? []).flatMap((r) => r.blockers.map((b) => ({ ...b, employee_label: employeeName(r.employee_id) }))),
    [preview, employeeName],
  );
  const unconfirmedLegalValueCount = allBlockers.filter((b) => b.kind === 'unconfirmed_legal_value').length;
  const plannedRows = useMemo(() => (preview?.results ?? []).filter((r) => r.grants.length > 0), [preview]);
  const plannedTotal = plannedRows.reduce((sum, r) => sum + r.grants.length, 0);

  return (
    <section className="space-y-3">
      <h2 className="text-h2">一括付与 (付与エンジンの実行)</h2>
      <p className="text-sm text-text-sub">
        指定した基準日時点で到来している付与を算出します。必ず試算してから内容を確認し、実行してください。
        すでに登録済みの付与は再実行しても二重には作られません。
      </p>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">{t('entity.leave_types', '休暇種別')}</label>
            <LeaveTypeSelector value={leaveTypeId} onChange={(v) => { setLeaveTypeId(v); resetPreview(); }} />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="accrual-as-of">基準日</label>
            <input id="accrual-as-of" type="date" value={asOf}
              onChange={(e) => { setAsOf(e.target.value); resetPreview(); }} className={dateInputCls} />
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-xs text-text-sub mb-1">対象</legend>
          <div className="flex flex-wrap gap-2">
            {([['all', '在籍している全員'], ['one', '社員を指定する']] as const).map(([mode, label]) => (
              <label key={mode}
                className={`inline-flex items-center gap-2 min-h-[44px] px-3 rounded border text-sm cursor-pointer ${
                  targetMode === mode ? 'border-text-body bg-surface-sunken text-text-body font-medium' : 'border-surface-border bg-white text-text-sub'
                }`}>
                <input type="radio" name="accrual-target" className="w-4 h-4" checked={targetMode === mode}
                  onChange={() => { setTargetMode(mode); resetPreview(); }} />
                {label}
              </label>
            ))}
          </div>
          {targetMode === 'one' && (
            <EmployeeSelector value={targetEmployeeId} onChange={(v) => { setTargetEmployeeId(v); resetPreview(); }} />
          )}
        </fieldset>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="secondary" loading={previewMut.isPending} disabled={!canPreview}
            onClick={() => previewMut.mutate()}>
            試算する
          </Button>
          <Button variant="primary" disabled={!preview || plannedTotal === 0 || unconfirmedLegalValueCount > 0}
            onClick={() => setConfirmOpen(true)}>
            この内容で付与する
          </Button>
        </div>
        {!preview && (
          <p className="text-xs text-text-sub">試算していない内容は実行できません。条件を変更すると試算結果は破棄されます。</p>
        )}
      </div>

      {previewMut.isPending && <div role="status" className="text-text-sub text-sm">試算中…</div>}

      {preview && (
        <div className="space-y-3">
          <p className="text-sm text-text-body">
            基準日 {preview.as_of} 時点の試算: <strong className="tabular-nums">{plannedTotal}</strong> 件の付与予定
            {allBlockers.length > 0 && <span className="text-text-sub">（実行できない事由 {allBlockers.length} 件）</span>}
          </p>

          {/* ⚠️ §6-3⑤ (D-279): 法定値の未確認を隠さない。未確認がある間は実行させない。 */}
          {unconfirmedLegalValueCount > 0 && (
            <div className="border border-alert-border bg-alert-bg rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-text-body">
                法定値が未確認のため、この条件では付与を実行できません ({unconfirmedLegalValueCount} 件)
              </p>
              <p className="text-sm text-text-sub">
                誤った日数での付与は法定の付与不足につながるため、確認済みの法定値が揃うまで実行を止めています。
                休暇種別の設定画面で法定値を確認してください。
              </p>
              <Link to="/settings/leave-types"
                className="inline-flex items-center gap-1 min-h-[44px] text-sm text-text-body underline underline-offset-2">
                休暇種別の設定へ <ExternalLink size={14} aria-hidden />
              </Link>
            </div>
          )}

          {allBlockers.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-text-body">実行できない事由</h3>
              <BlockerList blockers={allBlockers} />
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-text-body">付与の予定</h3>
            {plannedRows.length === 0 ? (
              <p className="text-sm text-text-sub">この基準日で新しく付与される内容はありません。</p>
            ) : (
              <ul className="space-y-2">
                {plannedRows.map((r) => (
                  <li key={r.employee_id} className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
                    <p className="text-sm font-medium text-text-body">{employeeName(r.employee_id)}</p>
                    {r.grants.map((g, i) => (
                      <div key={`${g.grantDate}-${i}`} className="border-t border-surface-line pt-2 space-y-2">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                          <dt className="text-text-sub">基準日</dt><dd className="text-text-body">{g.grantDate}</dd>
                          <dt className="text-text-sub">付与</dt>
                          <dd className="text-text-body tabular-nums">{formatDays(g.grantedDays, g.grantedMinutes)}</dd>
                          <dt className="text-text-sub">時効</dt><dd className="text-text-body">{g.expiresOn ?? '無期限'}</dd>
                        </dl>
                        {/* ⚠️ §6-3③: 日数だけを出さない。要約は常時可視、details は全項目の展開用。 */}
                        <BasisInlineSummary basis={g.basis} legalMeta={legalMeta} />
                        <details className="text-sm">
                          <summary className="cursor-pointer min-h-[44px] flex items-center text-text-sub">根拠の全項目</summary>
                          <div className="pt-1"><BasisSummary basis={g.basis} legalMeta={legalMeta} /></div>
                        </details>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="この内容で付与しますか"
        confirmLabel="付与する"
        loading={executeMut.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => executeMut.mutate()}
        message={(
          <div className="space-y-2 text-sm">
            <p>
              {leaveTypeLabel || '選択した休暇種別'} について、基準日 {asOf} 時点で
              <strong className="tabular-nums"> {plannedTotal} </strong>件の付与を登録します。
            </p>
            {allBlockers.length > 0 && (
              <p className="text-alert-text">
                実行できない事由が {allBlockers.length} 件あります。該当する社員には付与されません。
              </p>
            )}
            <p className="text-text-sub">登録後の取消は「付与の一覧」から行えます。</p>
          </div>
        )}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// 手動付与・初期残高の登録
// -----------------------------------------------------------------------------

function ManualGrantSection() {
  const t = useLabel();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [grantDate, setGrantDate] = useState(todayYmd());
  const [grantedDays, setGrantedDays] = useState('');
  const [grantedMinutes, setGrantedMinutes] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [note, setNote] = useState('');
  const [source, setSource] = useState('manual');

  const reset = () => {
    setEmployeeId(''); setLeaveTypeId(''); setGrantDate(todayYmd());
    setGrantedDays(''); setGrantedMinutes(''); setExpiresOn(''); setNote(''); setSource('manual');
  };

  const createMut = useMutation({
    mutationFn: () => apiFetch<{ grant_id: string }>('/leave/grants', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: employeeId,
        leave_type_id: leaveTypeId,
        grant_date: grantDate,
        granted_days: grantedDays === '' ? null : Number(grantedDays),
        granted_minutes: grantedMinutes === '' ? null : Number(grantedMinutes),
        expires_on: expiresOn === '' ? null : expiresOn,
        note: note === '' ? null : note,
        source,
      }),
    }),
    onSuccess: () => {
      toast.success('付与を登録しました');
      setOpen(false);
      reset();
      queryClient.invalidateQueries({ queryKey: ['leave-grants'] });
      queryClient.invalidateQueries({ queryKey: ['leave-me-balance'] });
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
      queryClient.invalidateQueries({ queryKey: ['leave-five-day-obligation'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '付与の登録に失敗しました'),
  });

  // サーバー側の assertManualGrantSane と同じ条件 (どちらか一方は必須)。押しても必ず 400 になる
  // 状態でボタンを有効にしない。
  const canSubmit = employeeId !== '' && leaveTypeId !== '' && grantDate !== ''
    && (grantedDays !== '' || grantedMinutes !== '');

  return (
    <section className="space-y-3">
      <h2 className="text-h2">手動付与・初期残高の登録</h2>
      <p className="text-sm text-text-sub">
        付与エンジンが扱わない休暇 (事由の発生で決まるもの等) や、他システムからの初期残高の移行を登録します。
        登録は追加のみで、既存の付与を置き換えません。
      </p>
      <Button variant="secondary" leftIcon={<Plus size={14} />} onClick={() => setOpen(true)}>
        付与を登録する
      </Button>

      <Modal
        open={open}
        title="付与の登録"
        onClose={() => setOpen(false)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={createMut.isPending}>キャンセル</Button>
            <Button variant="primary" loading={createMut.isPending} disabled={!canSubmit} onClick={() => createMut.mutate()}>
              登録する
            </Button>
          </>
        )}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">{t('entity.employees', '社員')}</label>
            <EmployeeSelector value={employeeId} onChange={setEmployeeId} />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">{t('entity.leave_types', '休暇種別')}</label>
            <LeaveTypeSelector value={leaveTypeId} onChange={setLeaveTypeId} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="manual-grant-date">基準日</label>
              <input id="manual-grant-date" type="date" value={grantDate}
                onChange={(e) => setGrantDate(e.target.value)} className={dateInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="manual-expires-on">時効 (空欄なら無期限)</label>
              <input id="manual-expires-on" type="date" value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)} className={dateInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="manual-granted-days">付与日数</label>
              <input id="manual-granted-days" type="number" min={0} step="0.5" inputMode="decimal" value={grantedDays}
                onChange={(e) => setGrantedDays(e.target.value)} className={numberInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="manual-granted-minutes">時間単位の付与 (分)</label>
              <input id="manual-granted-minutes" type="number" min={0} step={1} inputMode="numeric" value={grantedMinutes}
                onChange={(e) => setGrantedMinutes(e.target.value)} className={numberInputCls} />
            </div>
          </div>
          <p className="text-xs text-text-sub">付与日数と時間単位の付与のうち、少なくとも一方を入力してください。</p>
          <div>
            <label className="block text-xs text-text-sub mb-1">由来</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-full max-w-md min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MANUAL_SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="manual-note">備考</label>
            <textarea id="manual-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-surface-border rounded text-sm bg-white" />
          </div>
        </div>
      </Modal>
    </section>
  );
}

// -----------------------------------------------------------------------------
// 付与の一覧 (調整・取消)
// -----------------------------------------------------------------------------

function GrantListSection({
  canManage, legalMeta, filterEmployeeId,
}: { canManage: boolean; legalMeta: Map<string, LegalConstant>; filterEmployeeId: string }) {
  const queryClient = useQueryClient();
  const employeeName = useEmployeeNameResolver();
  const leaveTypeName = useLeaveTypeNameResolver();

  const [adjustTarget, setAdjustTarget] = useState<GrantRow | null>(null);
  const [adjustDays, setAdjustDays] = useState('');
  const [adjustMinutes, setAdjustMinutes] = useState('');
  const canAdjust = adjustDays !== '' || adjustMinutes !== '';
  const [adjustExpiresOn, setAdjustExpiresOn] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const [revokeTarget, setRevokeTarget] = useState<GrantRow | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const { data, isLoading, isError } = useQuery<{ grants: GrantRow[] }>({
    queryKey: ['leave-grants', filterEmployeeId],
    queryFn: () => apiFetch<{ grants: GrantRow[] }>(
      filterEmployeeId ? `/leave/grants?employee_id=${encodeURIComponent(filterEmployeeId)}` : '/leave/grants',
    ),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['leave-grants'] });
    queryClient.invalidateQueries({ queryKey: ['leave-consumptions'] });
    queryClient.invalidateQueries({ queryKey: ['leave-me-balance'] });
    queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
    queryClient.invalidateQueries({ queryKey: ['leave-five-day-obligation'] });
  };

  const openAdjust = (g: GrantRow) => {
    setAdjustTarget(g);
    setAdjustDays(g.granted_days === null ? '' : String(g.granted_days));
    setAdjustMinutes(g.granted_minutes === null ? '' : String(g.granted_minutes));
    setAdjustExpiresOn(g.expires_on ?? '');
    setAdjustNote(g.note ?? '');
  };

  /** ⚠️ `PUT /leave/grants/:id` は承認WFが有効なら 202 + status='pending_approval' を返す。
   *  「反映した」と「承認待ちになった」を混同させない (承認待ちなのに反映済みと伝えると、
   *  残数が変わっていないことに後から気づく事故になる)。 */
  const adjustMut = useMutation({
    mutationFn: () => apiFetch<{ grant_id: string; status?: string; approval_request_id?: string }>(
      `/leave/grants/${encodeURIComponent(adjustTarget!.grant_id)}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          granted_days: adjustDays === '' ? null : Number(adjustDays),
          granted_minutes: adjustMinutes === '' ? null : Number(adjustMinutes),
          expires_on: adjustExpiresOn === '' ? null : adjustExpiresOn,
          note: adjustNote === '' ? null : adjustNote,
        }),
      },
    ),
    onSuccess: (res) => {
      setAdjustTarget(null);
      if (res.status === 'pending_approval') {
        toast.info('調整を申請しました。承認されるまで残数は変わりません。');
      } else {
        toast.success('付与を調整しました');
      }
      invalidateAll();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '調整に失敗しました'),
  });

  const revokeMut = useMutation({
    mutationFn: () => apiFetch<{ grant_id: string; revoked: boolean }>(
      `/leave/grants/${encodeURIComponent(revokeTarget!.grant_id)}/revoke`,
      { method: 'POST', body: JSON.stringify({ reason: revokeReason === '' ? null : revokeReason }) },
    ),
    onSuccess: () => {
      toast.success('付与を取り消しました');
      setRevokeTarget(null);
      setRevokeReason('');
      invalidateAll();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '取消に失敗しました'),
  });

  return (
    <section className="space-y-3">
      <h2 className="text-h2">付与の一覧</h2>

      {isLoading &&<div role="status" className="text-text-sub text-sm">読み込み中…</div>}
      {isError && <p role="alert" className="text-sm text-alert-text">付与の一覧を取得できませんでした</p>}
      {/* ⚠️ §8-60: `data === undefined` を「付与が無い」と断定しない。 */}
      {!isLoading && !isError && !data && (
        <p role="status" className="text-sm text-text-sub">付与の一覧を取得できていません。再読み込みしてください。</p>
      )}
      {data && data.grants.length === 0 && <p className="text-sm text-text-sub">付与はまだありません。</p>}

      {data && data.grants.length > 0 && (
        <ul className="space-y-2">
          {data.grants.map((g) => (
            <li key={g.grant_id} className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-body break-words">{employeeName(g.employee_id)}</p>
                  <p className="text-xs text-text-sub break-words">{leaveTypeName(g.leave_type_id)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-text-sub border border-surface-border rounded px-2 py-0.5">
                    {GRANT_SOURCE_LABELS[g.source] ?? g.source}
                  </span>
                  {g.is_five_day_obligation_target && (
                    <span className="text-xs text-text-sub border border-surface-border rounded px-2 py-0.5">年5日義務の対象</span>
                  )}
                  {g.revoked_at !== null && (
                    <span className="text-xs text-alert-text border border-alert-border bg-alert-bg rounded px-2 py-0.5">取消済み</span>
                  )}
                </div>
              </div>

              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-text-sub">基準日</dt><dd className="text-text-body">{g.grant_date}</dd>
                <dt className="text-text-sub">付与</dt>
                <dd className="text-text-body tabular-nums">{formatDays(g.granted_days, g.granted_minutes)}</dd>
                <dt className="text-text-sub">時効</dt><dd className="text-text-body">{g.expires_on ?? '無期限'}</dd>
                {g.note && (<><dt className="text-text-sub">備考</dt><dd className="text-text-body break-words">{g.note}</dd></>)}
                {g.revoked_at !== null && (
                  <>
                    <dt className="text-text-sub">取消の理由</dt>
                    <dd className="text-text-body break-words">{g.revoked_reason ?? '(未記入)'}</dd>
                  </>
                )}
              </dl>

              {/* ⚠️ §6-3③: 一覧でも数値だけを出さない。要約は常時可視、details は全項目の展開用。 */}
              <BasisInlineSummary basis={g.basis} legalMeta={legalMeta} />
              {g.basis && (
                <details className="text-sm">
                  <summary className="cursor-pointer min-h-[44px] flex items-center text-text-sub">根拠の全項目</summary>
                  <div className="pt-1"><BasisSummary basis={g.basis} legalMeta={legalMeta} /></div>
                </details>
              )}

              {canManage && g.revoked_at === null && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="secondary" size="sm" onClick={() => openAdjust(g)}>調整する</Button>
                  <Button variant="secondary" size="sm" onClick={() => { setRevokeTarget(g); setRevokeReason(''); }}>
                    取り消す
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={adjustTarget !== null}
        title="付与の調整"
        onClose={() => setAdjustTarget(null)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setAdjustTarget(null)} disabled={adjustMut.isPending}>キャンセル</Button>
            {/* ⚠️ QA レビュー MEDIUM: 両欄を空にすると granted_days/minutes とも null が送られるが、
                サーバ側の adjustGrant は assertManualGrantSane 相当の検証を持たないため
                **日数ゼロの付与が黙って作られる**。手動登録 (canSubmit) と同じ条件で塞ぐ。 */}
            <Button variant="primary" loading={adjustMut.isPending} disabled={!canAdjust}
              onClick={() => adjustMut.mutate()}>調整する</Button>
          </>
        )}
      >
        <div className="space-y-3">
          <p className="text-sm text-text-sub">
            承認ワークフローが設定されている場合、この調整は申請として登録され、承認されるまで残数は変わりません。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="adjust-days">付与日数</label>
              <input id="adjust-days" type="number" min={0} step="0.5" inputMode="decimal" value={adjustDays}
                onChange={(e) => setAdjustDays(e.target.value)} className={numberInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="adjust-minutes">時間単位の付与 (分)</label>
              <input id="adjust-minutes" type="number" min={0} step={1} inputMode="numeric" value={adjustMinutes}
                onChange={(e) => setAdjustMinutes(e.target.value)} className={numberInputCls} />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="adjust-expires-on">時効 (空欄なら無期限)</label>
              <input id="adjust-expires-on" type="date" value={adjustExpiresOn}
                onChange={(e) => setAdjustExpiresOn(e.target.value)} className={dateInputCls} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="adjust-note">備考 (調整の理由)</label>
            <textarea id="adjust-note" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-surface-border rounded text-sm bg-white" />
          </div>
        </div>
      </Modal>

      {/* ⚠️ H-4: 付与の取消は年5日取得義務の未達を隠蔽できる操作。理由の入力を求め、その旨を明示する。 */}
      <ConfirmDialog
        open={revokeTarget !== null}
        title="この付与を取り消しますか"
        confirmLabel="取り消す"
        destructive
        loading={revokeMut.isPending}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => revokeMut.mutate()}
        message={(
          <div className="space-y-2 text-sm">
            <p>
              {revokeTarget ? `${employeeName(revokeTarget.employee_id)} の ${revokeTarget.grant_date} 付与 (${formatDays(revokeTarget.granted_days, revokeTarget.granted_minutes)})` : ''}
              を取り消します。残数が減ります。
            </p>
            <p className="text-alert-text">
              付与の取消は年5日取得義務の進捗を消すため、未達の状態が見えなくなることがあります。
              取消の理由は監査ログに記録されます。
            </p>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="revoke-reason">取消の理由</label>
              <textarea id="revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-surface-border rounded text-sm bg-white" />
            </div>
          </div>
        )}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// 消化の記録 (取消)
// -----------------------------------------------------------------------------

/**
 * ⚠️ 社員単位の一覧を主にする。`grant_id` は nullable のため、付与カードにぶら下げる構造だけに
 *    すると `grant_id=null` の消化行が画面から消える。
 * ⚠️ D-312: `leave_type_id` が null なのは leave_visibility ラダーで**伏せられた**結果であり
 *    欠損ではない。「(種別は非公開)」と表示し「不明」「-」にしない。
 * ⚠️ 対象社員の主所属ノードが未設定だと可視性が fail-safe の 'none' に倒れ行が1件も返らない。
 *    空配列を「消化が無い」と断定しない。
 */
function ConsumptionListSection({
  canManage, filterEmployeeId,
}: { canManage: boolean; filterEmployeeId: string }) {
  const queryClient = useQueryClient();
  const employeeName = useEmployeeNameResolver();
  const leaveTypeName = useLeaveTypeNameResolver();

  const [reverseTarget, setReverseTarget] = useState<ConsumptionRow | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  const { data, isLoading, isError } = useQuery<{ consumptions: ConsumptionRow[] }>({
    queryKey: ['leave-consumptions', filterEmployeeId],
    queryFn: () => apiFetch<{ consumptions: ConsumptionRow[] }>(
      filterEmployeeId ? `/leave/consumptions?employee_id=${encodeURIComponent(filterEmployeeId)}` : '/leave/consumptions',
    ),
  });

  const reverseMut = useMutation({
    mutationFn: () => apiFetch<{ consumption_id: string; reversed: boolean }>(
      `/leave/consumptions/${encodeURIComponent(reverseTarget!.consumption_id)}/reverse`,
      { method: 'POST', body: JSON.stringify({ reason: reverseReason === '' ? null : reverseReason }) },
    ),
    onSuccess: () => {
      toast.success('消化を取り消しました');
      setReverseTarget(null);
      setReverseReason('');
      queryClient.invalidateQueries({ queryKey: ['leave-consumptions'] });
      queryClient.invalidateQueries({ queryKey: ['leave-grants'] });
      queryClient.invalidateQueries({ queryKey: ['leave-me-balance'] });
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
      queryClient.invalidateQueries({ queryKey: ['leave-five-day-obligation'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '取消に失敗しました'),
  });

  return (
    <section className="space-y-3">
      <h2 className="text-h2">消化の記録</h2>
      <p className="text-sm text-text-sub">
        取得済みの休暇の記録です。誤って登録された消化はここから取り消せます (残数が戻ります)。
      </p>

      {isLoading && <div role="status" className="text-text-sub text-sm">読み込み中…</div>}
      {isError && <p role="alert" className="text-sm text-alert-text">消化の記録を取得できませんでした</p>}
      {/* ⚠️ §8-60: `data === undefined` を「記録が無い」と断定しない。 */}
      {!isLoading && !isError && !data && (
        <p role="status" className="text-sm text-text-sub">消化の記録を取得できていません。再読み込みしてください。</p>
      )}
      {data && data.consumptions.length === 0 && (
        <p className="text-sm text-text-sub">
          表示できる消化の記録がありません。実際に取得がない場合のほか、
          対象の社員の所属が未設定で表示範囲を判定できない場合もこの表示になります。
        </p>
      )}

      {data && data.consumptions.length > 0 && (
        <ul className="space-y-2">
          {data.consumptions.map((r) => (
            <li key={r.consumption_id} className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-body break-words">{employeeName(r.employee_id)}</p>
                  {/* ⚠️ D-312: null は欠損ではなく「伏せられている」。その旨が分かる表示にする。 */}
                  <p className="text-xs text-text-sub break-words">
                    {r.leave_type_id
                      ? leaveTypeName(r.leave_type_id)
                      : <span className="italic">(種別は非公開)</span>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-text-sub border border-surface-border rounded px-2 py-0.5">
                    {CONSUMPTION_UNIT_LABELS[r.unit] ?? r.unit}
                  </span>
                  {r.counts_toward_five_day_obligation && (
                    <span className="text-xs text-text-sub border border-surface-border rounded px-2 py-0.5">年5日義務にカウント</span>
                  )}
                  {r.reversed_at !== null && (
                    <span className="text-xs text-alert-text border border-alert-border bg-alert-bg rounded px-2 py-0.5">取消済み</span>
                  )}
                </div>
              </div>

              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-text-sub">取得日</dt><dd className="text-text-body">{r.consumed_on}</dd>
                <dt className="text-text-sub">消化</dt><dd className="text-text-body tabular-nums">{formatConsumed(r)}</dd>
                {(r.start_time || r.end_time) && (
                  <>
                    <dt className="text-text-sub">時刻</dt>
                    <dd className="text-text-body tabular-nums">{r.start_time ?? '—'} 〜 {r.end_time ?? '—'}</dd>
                  </>
                )}
                {r.reversed_at !== null && (
                  <>
                    <dt className="text-text-sub">取消の理由</dt>
                    <dd className="text-text-body break-words">{r.reversal_reason ?? '(未記入)'}</dd>
                  </>
                )}
              </dl>

              {/* ⚠️ 取消済みの行に再度の取消ボタンを出さない。 */}
              {canManage && r.reversed_at === null && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="secondary" size="sm" onClick={() => { setReverseTarget(r); setReverseReason(''); }}>
                    取り消す
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ H-4: 消化の取消は年5日取得義務の未達を隠蔽できる操作。理由の入力を求め、その旨を明示する。 */}
      <ConfirmDialog
        open={reverseTarget !== null}
        title="この消化を取り消しますか"
        confirmLabel="取り消す"
        destructive
        loading={reverseMut.isPending}
        onClose={() => setReverseTarget(null)}
        onConfirm={() => reverseMut.mutate()}
        message={(
          <div className="space-y-2 text-sm">
            <p>
              {reverseTarget ? `${employeeName(reverseTarget.employee_id)} の ${reverseTarget.consumed_on} の消化 (${formatConsumed(reverseTarget)})` : ''}
              を取り消します。取得しなかったことになり、残数が戻ります。
            </p>
            {reverseTarget?.counts_toward_five_day_obligation ? (
              <p className="text-alert-text">
                この消化は年5日取得義務にカウントされています。取り消すと取得日数が減り、
                未達の状態に戻ります。年5日取得義務 (労基法39条7項) の履行状況が変わる操作です。
              </p>
            ) : (
              <p className="text-alert-text">
                消化の取消は年5日取得義務の取得日数を減らすため、未達の状態が見えなくなることがあります。
              </p>
            )}
            <p className="text-text-sub">取消の理由は監査ログに記録されます。</p>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="reverse-reason">取消の理由</label>
              <textarea id="reverse-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-surface-border rounded text-sm bg-white" />
            </div>
          </div>
        )}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------

export function LeaveGrantsPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'leave_grant', 'manage', user?.non_inheriting_features);
  const canView = hasPermission(user?.effective_permissions, 'leave_grant', 'view', user?.non_inheriting_features);
  const legalMeta = useLegalConstantMeta();
  // 「付与の一覧」と「消化の記録」は同じ社員絞り込みに連動させる (どちらも社員単位の一覧)。
  const [filterEmployeeId, setFilterEmployeeId] = useState('');

  return (
    <div className="space-y-4">
      <h1 className="text-page-title text-text-main">休暇の付与</h1>
      <p className="text-sm text-text-sub">
        この画面では休暇の付与の実行・調整・取消を行います。残数・年5日取得義務・有休管理簿の確認は
        {/* ⚠️ 本文中のインラインリンクだが、タッチ幅ではタップ領域が 44px を下回るため
            inline-flex + min-h で高さを確保する (実機 320/375px で 112x20 を実測)。 */}
        <Link to="/leave/records"
          className="underline underline-offset-2 mx-1 inline-flex items-center min-h-[44px] sm:min-h-0">休暇の残数と記録</Link>
        画面です。
      </p>

      {!permsReady && <div role="status" className="text-text-sub">読み込み中…</div>}

      {permsReady && !canManage && (
        <div className="border border-surface-border rounded-lg bg-white p-4">
          <p className="text-sm text-text-body">休暇の付与を操作する権限がありません。</p>
          <p className="text-sm text-text-sub mt-1">
            付与・調整・取消はテナント全体の付与を管理する権限を持つ担当者のみが行えます。
          </p>
        </div>
      )}

      {permsReady && canManage && <AccrualRunSection legalMeta={legalMeta} />}
      {permsReady && canManage && <ManualGrantSection />}

      {permsReady && (canManage || canView) && (
        <>
          <div>
            <label className="block text-xs text-text-sub mb-1">{t('entity.employees', '社員')}で絞り込む</label>
            <EmployeeSelector value={filterEmployeeId} onChange={setFilterEmployeeId} allowEmpty emptyLabel="(すべての社員)" />
            <p className="text-xs text-text-sub mt-1">この絞り込みは「付与の一覧」と「消化の記録」の両方に適用されます。</p>
          </div>
          <GrantListSection canManage={canManage} legalMeta={legalMeta} filterEmployeeId={filterEmployeeId} />
          <ConsumptionListSection canManage={canManage} filterEmployeeId={filterEmployeeId} />
        </>
      )}
    </div>
  );
}
